import { SERVER_URL, AUTH_STORAGE_KEY } from "../socket";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, "+")
    .replace(/_/g, "/");
  const rawData = atob(base64);
  return Uint8Array.from([...rawData].map((c) => c.charCodeAt(0)));
}

function getToken() {
  try {
    const raw    = localStorage.getItem(AUTH_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed?.token || null;
  } catch {
    return null;
  }
}

export async function subscribeToPush() {
  try {
    // ── Guards ───────────────────────────────────────────────────────────────
    if (!("serviceWorker" in navigator)) {
      console.warn("[Push] Service Worker not supported");
      return false;
    }
    if (!("PushManager" in window)) {
      console.warn("[Push] PushManager not supported");
      return false;
    }
    if (
      window.location.protocol !== "https:" &&
      window.location.hostname !== "localhost"
    ) {
      console.warn("[Push] HTTPS required for push notifications");
      return false;
    }

    const token = getToken();
    if (!token) {
      console.warn("[Push] No auth token");
      return false;
    }

    // ── Fetch VAPID public key from server ───────────────────────────────────
    const keyRes = await fetch(`${SERVER_URL}/api/push/vapid-key`, {
      headers: { Authorization: `Bearer ${token}` },
    });

    if (!keyRes.ok) {
      console.warn("[Push] Failed to get VAPID key:", keyRes.status);
      return false;
    }

    const { publicKey } = await keyRes.json();
    if (!publicKey) {
      console.warn("[Push] No public key returned");
      return false;
    }

    // ── Request permission ───────────────────────────────────────────────────
    let permission = Notification.permission;

    if (permission === "denied") {
      console.warn("[Push] Notifications blocked by user");
      return false;
    }

    if (permission !== "granted") {
      permission = await Notification.requestPermission();
    }

    if (permission !== "granted") {
      console.warn("[Push] Permission not granted:", permission);
      return false;
    }

    // ── Get service worker registration ──────────────────────────────────────
    const reg = await navigator.serviceWorker.ready;

    // ── Unsubscribe stale subscription if keys changed ───────────────────────
    const existing = await reg.pushManager.getSubscription();
    if (existing) {
      // Check if our server still has this endpoint
      // If VAPID keys changed on server, old sub won't work — unsubscribe
      try {
        await existing.unsubscribe();
      } catch (_) {}
    }

    // ── Create fresh subscription ────────────────────────────────────────────
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly:      true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });

    // ── Save subscription to server ──────────────────────────────────────────
    const saveRes = await fetch(`${SERVER_URL}/api/push/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization:  `Bearer ${token}`,
      },
      body: JSON.stringify({ subscription }),
    });

    if (!saveRes.ok) {
      console.warn("[Push] Server save failed:", saveRes.status);
      return false;
    }

    console.log("[Push] ✅ Successfully subscribed on this device");

    // ── Listen for subscription change messages from SW ──────────────────────
    navigator.serviceWorker.addEventListener("message", async (event) => {
      if (event.data?.type === "PUSH_RESUBSCRIBE") {
        console.log("[Push] SW requested resubscription");
        await subscribeToPush();
      }
    });

    return true;
  } catch (err) {
    console.error("[Push] subscribeToPush error:", err.message);
    return false;
  }
}

export async function unsubscribeFromPush() {
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (sub) {
      await sub.unsubscribe();
      console.log("[Push] Unsubscribed");
    }
  } catch (err) {
    console.error("[Push] Unsubscribe error:", err.message);
  }
}